import axios from 'axios';

// Use relative /api in dev so the dev-server proxy forwards to backend (avoids CORS). Proxy target is 3002 by default (monolithic) or set REACT_APP_API_SERVER_URL=http://localhost:8002 for backend_app.
const explicitApiUrl = process.env.REACT_APP_API_SERVER_URL;
const baseURL = explicitApiUrl ? explicitApiUrl + '/api' : '/api';

// Create axios instance with proper configuration
const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    // Adding additional headers that might help with CORS
    'Accept': 'application/json'
  },
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
  // Set withCredentials based on environment
  withCredentials: false, // Changed to false by default
  // Increased timeout to handle slow backend responses
  timeout: 120000 // 120 seconds timeout (increased from 60)
});

// Add request interceptor to dynamically get the token before each request
axiosInstance.interceptors.request.use(
  config => {
    // When sending FormData (e.g. file upload), do NOT set Content-Type so the browser sets multipart/form-data with boundary
    if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Check if this is an analytics endpoint that's causing CORS issues
    if (config.url && (
      config.url.includes('/sdk/analytics/') ||
      config.url.includes('/intelligence/')
    )) {
      // For these endpoints, explicitly set withCredentials to false
      config.withCredentials = false;
    } else {
      // For all other endpoints, including team routes, use credentials
      config.withCredentials = true;
    }
    
    // Get token dynamically on each request - not from closure
    // Try both accessToken and token, as different parts of the app might use different keys
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Special handling for team routes
    if (config.url && config.url.includes('/team/')) {
      // Ensure content type is always set correctly for all team routes
      config.headers['Content-Type'] = 'application/json';
      
      // Ensure Authorization header is set for team routes
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Boards API: send current team so backend returns boards/pinned for the selected team
    if (config.url && config.url.includes('/boards')) {
      try {
        const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        const teamId = selectedTeamData.id ?? selectedTeamData._id ?? null;
        if (teamId != null && teamId !== '') {
          config.headers['X-Team-Id'] = String(teamId);
        }
      } catch (_) {}
    }
    
    return config;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

function isCanceledRequest(error) {
  if (!error) return false;
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
    return true;
  }
  const msg = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  return msg === 'canceled' || msg === 'cancelled';
}

// Add response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // AbortController / duplicate in-flight request — not a real failure; do not log
    if (isCanceledRequest(error)) {
      return Promise.reject(error);
    }
    
    // Check if this is a network error (no response from server)
    const isNetworkError = !error.response && (
      error.message === 'Network Error' || 
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ETIMEDOUT' ||
      error.message?.includes('Failed to fetch')
    );
    
    // For network errors, suppress console errors and handle them more gracefully
    if (isNetworkError) {
      // Implement retry logic for network errors (but not timeouts)
      if (!originalRequest._retry && error.code !== 'ECONNABORTED') {
        originalRequest._retry = true;
        
        // Add a small delay before retrying
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Return a new request
        return axiosInstance(originalRequest);
      }
      
      // After retry fails, return a more user-friendly error
      // This prevents the error from bubbling up to React's error overlay
      const networkError = new Error('Unable to connect to server. Please check your connection or try again later.');
      networkError.isNetworkError = true;
      networkError.suppressErrorOverlay = true; // Flag to suppress React error overlay
      networkError.originalError = error;
      return Promise.reject(networkError);
    }
    
    // For non-network errors, log normally
    console.error('API Response Error:', {
      url: originalRequest?.url,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data 
    });
    
    // Handle timeout errors specifically
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      // Don't retry timeout errors immediately - let the calling code handle it
      return Promise.reject(error);
    }
    
    // Handle 401 errors (token expired)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshResponse = await axios.post(
          `${baseURL}/auth/refresh-token`, 
          {}, 
          { withCredentials: true }
        );
        
        if (refreshResponse.data.accessToken) {
          // Save the new access token
          localStorage.setItem("accessToken", refreshResponse.data.accessToken);
          
          // Update the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
          
          // Retry the original request
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // If refresh token also fails, logout the user
        localStorage.removeItem("accessToken");
        localStorage.removeItem("User");
        localStorage.removeItem("selectedTeam");
        localStorage.removeItem("selectedTeamData");
        localStorage.removeItem("selectedWebsite");
        localStorage.removeItem("subscriptionStatus");
        
        // Redirect to login page if not already there
        if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
          window.location.href = '/login';
        }
      }
    }
    
    // Log the error for debugging
    console.error("API Error:", error.message);
    
    if (error.response && error.response.status === 401) {
      // Handle unauthorized access
      // Redirect to login page if unauthorized
      if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("User");
        localStorage.removeItem("selectedTeam");
        localStorage.removeItem("selectedTeamData");
        localStorage.removeItem("selectedWebsite");
        localStorage.removeItem("subscriptionStatus");
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;